import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'

interface Game {
  id: string
  slug: string
  status: string
  prizes_json: { first: number; second: number; third: number }
  created_at: string
  player_count: number
}

interface Props {
  onOpenGame: (slug: string) => void
  onNewGame: () => void
}

export default function GameDashboard({ onOpenGame, onNewGame }: Props) {
  const { user, token, logout } = useAuth()
  const [games,   setGames]   = useState<Game[]>([])
  const [loading, setLoading] = useState(true)

  const loadGames = () => {
    fetch('/api/games', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setGames(data.games ?? []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(loadGames, [token])

  const createGame = async () => {
    const res  = await fetch('/api/games', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ prizes: { first: 50, second: 30, third: 20 } })
    })
    const data = await res.json()
    if (data.game) onOpenGame(data.game.slug)
  }

  const deleteGame = async (slug: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm(`Delete game "${slug}"? This cannot be undone.`)) return
    await fetch(`/api/games/${slug}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    })
    setGames(prev => prev.filter(g => g.slug !== slug))
  }

  const resetToPrerace = async (slug: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm(`Reset "${slug}" back to Pre-Race status?`)) return
    await fetch(`/api/games/${slug}/reset`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    })
    setGames(prev => prev.map(g => g.slug === slug ? { ...g, status: 'prerace' } : g))
  }

  const statusLabel = (s: string) => ({
    setup:    { label: 'Setup',     color: '#888' },
    prerace:  { label: 'Pre-Race',  color: '#e8b84b' },
    live:     { label: '🔴 LIVE',   color: '#c81026' },
    finished: { label: 'Finished',  color: '#4caf50' },
  }[s] ?? { label: s, color: '#888' })

  // Race day is May 24 2026 — before then, LIVE is considered incorrect
  const raceDay = new Date('2026-05-24T16:00:00Z')
  const beforeRaceDay = new Date() < raceDay

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '1.5rem 1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.6rem' }}>
        <div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff' }}>Welcome, {user?.name}</div>
          <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)' }}>{user?.email}</div>
        </div>
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <button className="btn btn-primary" onClick={createGame}>+ New Game</button>
          <button className="btn btn-secondary" onClick={logout} style={{ fontSize: '0.78rem' }}>Sign Out</button>
        </div>
      </div>

      {/* Game list */}
      <div className="section-title">Your Games</div>
      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', padding: '2rem', textAlign: 'center' }}>
          Loading…
        </div>
      ) : games.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.8rem' }}>🏁</div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', marginBottom: '1.2rem' }}>
            No games yet. Create one to get started.
          </div>
          <button className="btn btn-primary" onClick={createGame}>Create Your First Game</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
          {games.map(g => {
            const { label, color } = statusLabel(g.status)
            const shareUrl = `${window.location.origin}/game/${g.slug}`
            const isStuckLive = g.status === 'live' && beforeRaceDay
            return (
              <div
                key={g.id}
                className="card"
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.9rem 1.2rem', flexWrap: 'wrap' }}
                onClick={() => onOpenGame(g.slug)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{g.slug}</span>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color, background: `${color}22`, borderRadius: 10, padding: '0.15rem 0.5rem' }}>
                      {label}
                    </span>
                    {isStuckLive && (
                      <span style={{ fontSize: '0.65rem', color: '#e8b84b', background: 'rgba(232,184,75,0.1)', borderRadius: 8, padding: '0.1rem 0.4rem' }}>
                        started early
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', marginTop: '0.2rem' }}>
                    {g.player_count} players · ${(g.prizes_json?.first ?? 0) + (g.prizes_json?.second ?? 0) + (g.prizes_json?.third ?? 0)} pool
                    · {new Date(g.created_at).toLocaleDateString()}
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem' }}
                    onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(shareUrl).then(() => alert('Link copied!')) }}
                  >
                    📋 Share
                  </button>
                  {isStuckLive && (
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem', borderColor: '#e8b84b', color: '#e8b84b' }}
                      onClick={e => resetToPrerace(g.slug, e)}
                      title="Reset game status back to Pre-Race"
                    >
                      ↩ Reset
                    </button>
                  )}
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem', borderColor: '#c81026', color: '#c81026' }}
                    onClick={e => deleteGame(g.slug, e)}
                    title="Delete this game"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
